'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import { SessionContext } from '@benzinga/session-context';
import { useSophiTracking } from '@benzinga/user-context';
import { UserManager } from '@benzinga/user-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import { MetaProps } from '@benzinga/seo';
import { ClickTracker, PageTracker } from '@benzinga/analytics';
import { NoFirstRender } from '@benzinga/hooks';
import { sophiManager } from '@benzinga/ads-utils';

import { applyRouteMetaTargeting } from '../../_libs/site-shell/applyRouteMetaTargeting';
import { resolvePageAnalytics } from '../../_libs/site-shell/pageAnalytics';
import { ChartbeatAnalytics } from '../root/ChartbeatAnalytics';
import type { SitePageConfig } from './pageConfig';
import { useRouteMeta } from './RouteMetaContext';

interface PageTrackingProps {
  pageConfig?: SitePageConfig;
}

export default function PageTracking({ pageConfig = {} }: PageTrackingProps) {
  const pathname = usePathname();
  const routeMeta = useRouteMeta();
  const session = React.useContext(SessionContext);
  const userManager = session.getManager(UserManager);
  const trackingManager = session.getManager(TrackingManager);
  const [scrollMeta, setScrollMeta] = useState<MetaProps | null>(null);
  const [isSophiReady, setIsSophiReady] = useState(false);
  const lastPathnameRef = useRef(pathname);

  const analytics = useMemo(
    () => resolvePageAnalytics({ routeMeta, pageConfig, pathname, scrollMeta }),
    [routeMeta, pageConfig, pathname, scrollMeta],
  );

  const { shouldTrack, meta, chartbeat, payload } = analytics;

  trackingManager.setMeta(meta);

  useEffect(() => {
    if (lastPathnameRef.current !== pathname) {
      setScrollMeta(null);
      lastPathnameRef.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<{ meta?: MetaProps; trackMeta?: boolean }>;
      if (customEvent?.detail?.trackMeta === false) return;
      const nextMeta = customEvent.detail?.meta;
      if (!nextMeta) return;
      setScrollMeta(nextMeta);
      trackingManager.setMeta(nextMeta);
    };

    window.addEventListener('page-transition', listener);
    return () => window.removeEventListener('page-transition', listener);
  }, [trackingManager]);

  useEffect(() => {
    applyRouteMetaTargeting(payload);
  }, [payload]);

  const { trackPageView } = useSophiTracking({
    article: payload.article,
    meta,
    post: payload.post,
    term: payload.term,
    pageTargeting: payload.pageTargeting,
  });

  useEffect(() => {
    (async () => {
      sophiManager.init(async () => {
        setIsSophiReady(true);
        const user = await global.analytics?.user();
        const anonymousId = user?.anonymousId();
        const userId = user?.id();
        if (userId || anonymousId) {
          const segmentUserTraits = await userManager.getSegmentUserTraits({
            userId: userId ?? undefined,
            anonymousId: anonymousId ?? undefined,
          });
          if (segmentUserTraits.ok) {
            await sophiManager.setUserProperties(segmentUserTraits.ok);
          }
        }
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSophiReady || !shouldTrack) return;
    trackPageView();
  }, [isSophiReady, shouldTrack, pathname, scrollMeta, meta, trackPageView]);

  return (
    <>
      {chartbeat ? <ChartbeatAnalytics authorName={chartbeat.authorName} sections={chartbeat.sections} /> : null}
      <NoFirstRender>
        <React.Suspense fallback={null}>
          <ClickTracker article={payload.article} post={payload.post} />
        </React.Suspense>
      </NoFirstRender>
      {shouldTrack && isSophiReady ? <PageTracker enableCoralogixRum={true} routerType="app" /> : null}
    </>
  );
}
